import React from 'react';
import { Provider } from 'react-redux';
import { combineReducers, createStore, Store } from 'redux';
import { createEpicMiddleware } from 'redux-observable';

import { Action } from '../../../actions';
import { canvasEditorReducer } from '../../../reducers/canvasEditor';
import { Tld } from '../../../typings/common';
import { PcodeAds } from '../../../typings/pcode';
import { CanvasEditorState } from '../../../typings/state';
import { CanvasApi } from '../../api/CanvasApi';
import { CanvasAppContainer } from '../../CanvasApp/CanvasAppContainer';
import { canvasEpics } from '../../common/epics';
import { applyReduxMiddleware, getPresetId, isCreateImmediately } from '../../common/helpers';
import { getCanvasInitialState } from '../../common/reducers';
import { CreativeType, Page, SharedData } from '../../common/typings';
import { hideBusy } from '../../Html5App/actions';
import { getInitialHtml5AppState } from '../../Html5App/reducers';

interface CanvasAppWrapperProps {
    sharedData: SharedData;
    onClose: () => void;
    onCreativesSelect: (ids: number[]) => void;
    tld: Tld;
    videoPreviewData?: PcodeAds;
    page: Page;
    creativeId?: string;
}

interface CanvasEpicDeps {
    canvasApi: CanvasApi;
}

interface WrapperState {
    canvasEditor: CanvasEditorState;
    commonData: {
        tld: Tld;
    };
}

function getCommonData(state: { tld: Tld } = { tld: Tld.ru }): { tld: Tld } {
    return state;
}

export class CanvasAppWrapper extends React.PureComponent<CanvasAppWrapperProps> {
    private readonly store: Store<WrapperState, Action>;

    constructor(props: CanvasAppWrapperProps) {
        super(props);

        const epicDeps = this.getEpicDeps();
        const epicMiddleware = createEpicMiddleware<Action, Action, WrapperState, CanvasEpicDeps>({
            dependencies: epicDeps
        });

        this.store = createStore<WrapperState, Action, {}, {}>(
            combineReducers({ canvasEditor: canvasEditorReducer, commonData: getCommonData }),
            this.buildPreloadedState(),
            applyReduxMiddleware(epicMiddleware)
        );

        epicMiddleware.run(canvasEpics);
    }

    render(): React.ReactNode {
        const { onClose, onCreativesSelect, videoPreviewData } = this.props;

        return (
            <Provider store={this.store}>
                <CanvasAppContainer
                    onClose={onClose}
                    onCreativesSelect={onCreativesSelect}
                    videoPreviewData={videoPreviewData}
                />
            </Provider>
        );
    }

    public unlock = (): void => {
        this.store.dispatch(hideBusy());
    };

    private buildPreloadedState = (): WrapperState => {
        const sharedData = this.normalizeSharedData(this.props.sharedData);
        const canvasInitialState = getCanvasInitialState(sharedData);
        canvasInitialState.commonData.page = this.props.page;

        // если пришел id креатива, открываем конструктор для заданного креатива
        if (this.props.creativeId) {
            canvasInitialState.commonData.page = Page.CONSTRUCTOR;
            canvasInitialState.addCreative.creativeId = this.props.creativeId;
        }

        // для страницы конструктора без id креатива нужно получать дефолтный presetId
        if (this.props.page === Page.CONSTRUCTOR && !this.props.creativeId) {
            canvasInitialState.addCreative.presetId = getPresetId(sharedData);
            canvasInitialState.addCreative.returnPage = Page.CREATIVES;
        }

        // при открытии пресетов для cpm, cpm_audio и тд нужно открывать страничку конструктора
        if (this.props.page === Page.PRESETS && isCreateImmediately(sharedData)) {
            canvasInitialState.addCreative.presetId = getPresetId(sharedData);
            canvasInitialState.addCreative.returnPage = Page.CREATIVES;
            canvasInitialState.commonData.page = Page.CONSTRUCTOR;
        }

        return {
            canvasEditor: {
                sharedData,
                html5: getInitialHtml5AppState(sharedData),
                itemNameEditors: {},
                canvas: canvasInitialState
            },
            commonData: {
                tld: this.props.tld
            }
        };
    };

    private getEpicDeps(): {
        canvasApi: CanvasApi;
    } {
        return {
            canvasApi: new CanvasApi()
        };
    }

    private normalizeSharedData(sharedData: SharedData): SharedData {
        const res = { ...sharedData };
        if (!res.creativeType) {
            res.creativeType = CreativeType.COMMON;
        } else if (res.creativeType === CreativeType.VIDEO_ADDITION_BAD) {
            res.creativeType = CreativeType.VIDEO_ADDITION;
        } else if (res.creativeType === CreativeType.CPM_GEOPRODUCT_BAD) {
            res.creativeType = CreativeType.CPM_GEOPRODUCT;
        }
        return res;
    }
}
